import {
  allocateBoundedBudgetUnits,
  type CampaignCreationCapabilities,
  type SmartCreationReviewEditChange,
  type SmartCreationReviewTarget,
  type SmartCreationReviewWorkspace,
  type SmartCreationStrategyTargetIntent,
  smartCreationStrategyTargetLane,
} from "@adpilot/shared";

export function isOptimisticReviewChange(change: SmartCreationReviewEditChange): boolean {
  return (
    change.operation === "set_campaign_excluded" || change.operation === "set_ai_action_policy"
  );
}

export class ReviewProjectionError extends Error {
  constructor(readonly code: "review_all_campaigns_excluded" | "review_budget_invalid") {
    super(code);
  }
}

function intent(target: SmartCreationReviewTarget): SmartCreationStrategyTargetIntent {
  switch (target.type) {
    case "keyword":
      return { type: "keyword", keyword: target.keyword, matchType: target.matchType.effective };
    case "automatic":
      return { type: "auto", expressionType: target.expressionType };
    case "product":
      return { type: "product", asin: target.asin, matchType: target.matchType ?? "exact" };
    case "category":
      return { type: "category", categoryId: target.categoryId };
    case "category_brand":
      return { type: "category_brand", categoryId: target.categoryId, brandId: target.brandId };
  }
}

/** A display projection never invents a server revision, signature or confirmation hash. */
export function projectOptimisticReview(
  base: SmartCreationReviewWorkspace,
  changes: readonly SmartCreationReviewEditChange[],
  capabilities?: CampaignCreationCapabilities,
): SmartCreationReviewWorkspace {
  const workspace = structuredClone(base);
  const { graph } = workspace;
  for (const change of changes) {
    if (change.operation === "set_ai_action_policy") {
      workspace.aiActionPolicy = change.policy;
      continue;
    }
    if (change.operation !== "set_campaign_excluded") continue;
    const selected = graph.campaigns.find(
      (campaign) => campaign.ref.lineageKey === change.ref.lineageKey,
    );
    if (!selected || selected.excluded.effective === change.excluded) continue;
    if (!capabilities) throw new ReviewProjectionError("review_budget_invalid");
    const included = graph.campaigns.filter((campaign) => !campaign.excluded.effective);
    const recipients = included.filter((campaign) => campaign !== selected);
    if (!recipients.length) throw new ReviewProjectionError("review_all_campaigns_excluded");
    const limits = capabilities.dailyBudget;
    const scale = 10 ** limits.decimals;
    const total = included.reduce(
      (sum, campaign) => sum + Math.round(campaign.dailyBudget.effective * scale),
      0,
    );
    const units = allocateBoundedBudgetUnits(
      recipients.map((campaign) => Math.round(campaign.dailyBudget.effective * scale)),
      change.excluded ? total : total - Math.round(selected.dailyBudget.effective * scale),
      Math.ceil(limits.min * scale),
      Math.floor(limits.max * scale),
    );
    if (!units) throw new ReviewProjectionError("review_budget_invalid");
    for (const [index, campaign] of recipients.entries()) {
      const amount = units[index]! / scale;
      campaign.dailyBudget = {
        effective: amount,
      };
      campaign.budgetAllocation = {
        effective: { basis: "amount", amount },
      };
    }
    selected.excluded = {
      effective: change.excluded,
    };
  }
  const included = graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  workspace.summary.includedCampaignCount = included.length;
  workspace.summary.excludedCampaignCount = graph.campaigns.length - included.length;
  const scale = 10 ** (capabilities?.dailyBudget.decimals ?? 2);
  workspace.summary.totalDailyBudget =
    included.reduce(
      (sum, campaign) => sum + Math.round(campaign.dailyBudget.effective * scale),
      0,
    ) / scale;
  for (const campaign of workspace.draftContent.campaigns) {
    const reviewed = graph.campaigns.find((item) => item.ref.itemKey === campaign.itemKey);
    if (reviewed) campaign.dailyBudget = reviewed.dailyBudget.effective;
  }
  if (workspace.strategyIntentCoverage) {
    const includedKeys = new Set(included.map((campaign) => campaign.ref.lineageKey));
    workspace.pendingStrategyLanes = [
      ...new Set(
        workspace.strategyIntentCoverage
          .filter(
            (coverage) =>
              !graph.targets.some((target) => {
                if (
                  target.excluded.effective ||
                  smartCreationStrategyTargetLane(target.strategyKind, intent(target)) !==
                    coverage.lane
                )
                  return false;
                const group = graph.adGroups.find(
                  (item) => item.ref.lineageKey === target.adGroupLineageKey,
                );
                return (
                  group &&
                  includedKeys.has(group.campaignLineageKey) &&
                  group.products.some((product) => coverage.productAsins.includes(product.asin))
                );
              }),
          )
          .map((coverage) => coverage.lane),
      ),
    ];
  }
  return workspace;
}
